import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import {
  Users, Store, ShoppingBag, Gem, Swords, Shield, Share2, Ban, TrendingUp, UserPlus,
} from 'lucide-react';
import { api, compact, money } from '../../lib/api';
import { SkeletonList, SectionHead } from '../../components/ui';

export default function Overview() {
  const { data, isLoading } = useQuery({ queryKey: ['adminOverview'], queryFn: () => api.get('/admin/overview').then(r => r.data) });
  if (isLoading || !data) return <SkeletonList n={4} h="h-28" />;
  const s = data.stats;

  const chart = data.daily.map(d => ({
    d: new Date(d.d).toLocaleDateString('en', { day: '2-digit', month: 'short' }),
    revenue: Number(d.revenue), points: Number(d.points), orders: d.purchases,
  }));
  const signups = data.signups.map(d => ({ d: new Date(d.d).toLocaleDateString('en', { day: '2-digit', month: 'short' }), users: d.c }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <K icon={Users} l="Users" v={compact(s.users)} s={`+${s.new_7d} last 7d`} c="bg-blue-500" />
        <K icon={Store} l="Shops" v={compact(s.shops_approved)} s={`${s.shops_pending} pending`} c="bg-mint-500" highlight={s.shops_pending > 0} />
        <K icon={ShoppingBag} l="GMV" v={money(s.gmv)} s={`${compact(s.purchases)} orders`} c="bg-brand-500" />
        <K icon={Gem} l="Points issued" v={compact(s.points_issued)} s={`${compact(s.points_spent)} spent`} c="bg-grape-500" />
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <M icon={UserPlus} l="Buyers" v={compact(s.buyers)} />
        <M icon={Store} l="Producers" v={compact(s.producers)} />
        <M icon={Swords} l="Fights" v={compact(s.fights)} />
        <M icon={Shield} l="Guilds" v={compact(s.guilds)} />
        <M icon={Share2} l="Shares" v={compact(s.shares)} />
        <M icon={Ban} l="IP bans" v={s.ip_bans} />
      </div>

      <div className="card p-4">
        <SectionHead title="Revenue & points · 30 days" />
        <ResponsiveContainer width="100%" height={210}>
          <AreaChart data={chart}>
            <defs>
              <linearGradient id="a1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} /><stop offset="100%" stopColor="#f97316" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="a2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} /><stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="d" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area name="Revenue" type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2.5} fill="url(#a1)" />
            <Area name="Points" type="monotone" dataKey="points" stroke="#8b5cf6" strokeWidth={2} fill="url(#a2)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="card p-4">
        <SectionHead title="New users · 30 days" />
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={signups}>
            <defs><linearGradient id="a3" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="d" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
            <Area type="monotone" dataKey="users" stroke="#10b981" strokeWidth={2.5} fill="url(#a3)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function K({ icon: Icon, l, v, s, c, highlight }) {
  return (
    <div className={`card p-3 ${highlight ? 'ring-2 ring-amber-400' : ''}`}>
      <div className={`w-8 h-8 rounded-lg ${c} grid place-items-center text-white mb-2`}><Icon size={15} /></div>
      <p className="text-[10px] font-black text-ink-400 uppercase">{l}</p>
      <p className="font-black text-lg leading-tight">{v}</p>
      <p className="text-[10px] text-ink-400 font-semibold">{s}</p>
    </div>
  );
}
function M({ icon: Icon, l, v }) {
  return (
    <div className="card p-2.5 text-center">
      <Icon size={13} className="mx-auto text-ink-400 mb-1" />
      <p className="font-black text-sm">{v}</p>
      <p className="text-[9px] font-bold text-ink-400 uppercase">{l}</p>
    </div>
  );
}